import React, {useState} from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Principal, PrincipalType} from 'parties/PartyConstants';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {EntityWithText} from 'ui-library/entity-input/EntityInputConstants';
import {UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import {SingleEntityInput} from 'ui-library/entity-input/SingleEntityInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {isEnterKey} from 'util/Keyboard';
import {assertNever} from 'util/Types';

import {Member} from './OrganizationConstants';

export interface AddTeamMemberInputProps {
  type: PrincipalType;
  organizationId: string;
  team: {id: string};
  existingMembers?: Array<Member>;
}

function getAddMemberText(type: PrincipalType): string {
  switch (type) {
    case ModelTypeConstants.BENCHLING_APP:
      return 'Add App';
    case ModelTypeConstants.ROUTINE:
      return 'Add Agent';
    case ModelTypeConstants.USER:
      return 'Add Member';
    default:
      return assertNever(type);
  }
}

function getSearchPlaceholder(type: PrincipalType): string {
  switch (type) {
    case ModelTypeConstants.BENCHLING_APP:
      return 'Search for an app';
    case ModelTypeConstants.ROUTINE:
      return 'Search for an agent';
    case ModelTypeConstants.USER:
      return 'Search for a user';
    default:
      return assertNever(type);
  }
}

function getUserSearchType(type: PrincipalType): UserSearchType {
  switch (type) {
    case ModelTypeConstants.USER:
      return UserSearchType.NotSuspended;
    case ModelTypeConstants.BENCHLING_APP:
      // TODO(BNCH-172732): Decouple App search from user search so App-only search can omit users.
      return UserSearchType.NotSuspended;
    default:
      return UserSearchType.None;
  }
}

export default function AddTeamMemberInput({
  team,
  existingMembers,
  organizationId,
  type,
}: AddTeamMemberInputProps): JSX.Element {
  const [isAddingMember, setIsAddingMember] = useState(false);

  async function handleAddTeamMember({entity}: {entity?: Principal}): Promise<void> {
    if (!entity || entity.type !== type) {
      return;
    }
    setIsAddingMember(true);
    try {
      await OrganizationActions.addTeamMember(team.id, entity);
    } finally {
      setIsAddingMember(false);
    }
  }

  function filterExistingMembersAndIncorrectType(
    text: string,
    options: Array<EntityWithText>
  ): Array<EntityWithText> {
    options = options.filter((o) => o.type === type);
    if (!existingMembers) {
      return options;
    }
    const existingMemberIds = existingMembers.map((m) => m.id);
    return options.filter((o) => !existingMemberIds.includes(o.id));
  }

  return (
    <SingleEntityInput
      addPredicate={isEnterKey}
      allowEmails={false}
      allowTeams={false}
      allowOrganizations={false}
      allowAgents={type === ModelTypeConstants.ROUTINE}
      disabled={isAddingMember}
      size={TagsInputSize.SMALL}
      allowBenchlingApps={type === ModelTypeConstants.BENCHLING_APP}
      buttonProps={{
        buttonStyle: ButtonStyle.SECONDARY,
        size: ButtonSize.SMALL,
        text: getAddMemberText(type),
      }}
      filterOptions={filterExistingMembersAndIncorrectType}
      onSubmit={handleAddTeamMember}
      organizationIds={[organizationId]}
      userSearchType={getUserSearchType(type)}
      placeholder={getSearchPlaceholder(type)}
    />
  );
}
